import React, { useState, useEffect } from 'react';
import {
  Modal,
  ConfirmDialog,
  Pagination,
  AccessDeniedMessage,
  AccessDeniedModalContent,
} from '../../molecules';
import { CommentSearchBar } from '../../molecules/CommentSearchBar';
import { CommentsPageHeader } from '../../molecules/CommentsPageHeader';
import { CommentsList } from '../../organisms';
import { commentsService, type Comment } from '../../../services/commentsService';

export const CommentsPage = () => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [requiredRoles, setRequiredRoles] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<Comment | null>(null);
  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');
  const [accessDeniedRoles, setAccessDeniedRoles] = useState<string[]>([]);

  const loadComments = async (page: number = 1, search?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await commentsService.searchComments(search, page, 20);
      setComments(response.data);
      //console.log(response.data);
      setTotalPages(response.totalPages);
      setCurrentPage(response.page);
    } catch (err: any) {
      if (err?.response?.status === 403 || err?.message === 'Erişim Reddedildi') {
        setIsAccessDenied(true);
        setError(null);
        const roles = err?.response?.data?.requiredRoles || ['superuser', 'moderator'];
        setRequiredRoles(Array.isArray(roles) ? roles : []);
      } else {
        setError(
          err?.response?.data?.message ||
          err?.message ||
          'Yorumlar yüklenirken bir hata oluştu',
        );
        setIsAccessDenied(false);
      }
      console.error('Failed to load comments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComments(1, searchQuery);
  }, [searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadComments(1, searchQuery);
  };

  const handleDeleteClick = (comment: Comment) => {
    setCommentToDelete(comment);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!commentToDelete) return;

    try {
      setDeletingId(commentToDelete.id);
      setShowDeleteConfirm(false);
      await commentsService.deleteComment(commentToDelete.id);

      const shouldGoToPreviousPage = comments.length === 1 && currentPage > 1;
      const pageToLoad = shouldGoToPreviousPage ? currentPage - 1 : currentPage;
      await loadComments(pageToLoad, searchQuery);
    } catch (err: any) {
      console.error('Failed to delete comment:', err);
      if (err?.response?.status === 403 || err?.message === 'Erişim Reddedildi') {
        const roles = err?.response?.data?.requiredRoles || ['superuser'];
        setAccessDeniedMessage('Bu yorumu silmek için gerekli yetkilere sahip değilsiniz.');
        setAccessDeniedRoles(Array.isArray(roles) ? roles : []);
        setShowAccessDeniedModal(true);
      } else {
        alert(err instanceof Error ? err.message : 'Yorum silinirken bir hata oluştu');
      }
    } finally {
      setDeletingId(null);
      setCommentToDelete(null);
    }
  };

  if (isAccessDenied) {
    return (
      <AccessDeniedMessage
        message="Bu yorumlara erişmek için gerekli yetkilere sahip değilsiniz."
        requiredRoles={requiredRoles}
        onBack={() => window.history.back()}
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-gray-50 to-white min-h-screen">
      <CommentsPageHeader />

      <CommentSearchBar
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        onSearch={handleSearch}
      />

      {loading ? (
        <div className="text-center text-gray-500 py-8">Yorumlar yükleniyor...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : comments.length === 0 ? (
        <div className="text-center text-gray-500 py-8">Yorum bulunamadı</div>
      ) : (
        <>
          <CommentsList
            comments={comments}
            deletingId={deletingId}
            onDeleteClick={handleDeleteClick}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => loadComments(page, searchQuery)}
          />
        </>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setCommentToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Yorumu Sil"
        message={
          <div>
            {commentToDelete?.content && (
              <p className="mb-3 text-sm text-gray-600 line-clamp-3">
                {commentToDelete.content}
              </p>
            )}
            <p className="text-gray-700">
              Bu yorumu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
          </div>
        }
        confirmText="Sil"
        cancelText="İptal"
        variant="danger"
        isLoading={deletingId !== null}
      />

      <Modal
        isOpen={showAccessDeniedModal}
        onClose={() => setShowAccessDeniedModal(false)}
        title="Erişim Reddedildi"
        size="medium"
      >
        <AccessDeniedModalContent
          message={accessDeniedMessage}
          requiredRoles={accessDeniedRoles}
          onClose={() => setShowAccessDeniedModal(false)}
        />
      </Modal>
    </div>
  );
};
